import React from "react";

interface ExportButtonProps {
  onClick: () => void;
  isLoading: boolean;
  isRateLimited: boolean;
  remainingExports: number;
  countdown: number | null;
  disabled?: boolean;
}

const formatCountdown = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

export const ExportButton: React.FC<ExportButtonProps> = ({
  onClick,
  isLoading,
  isRateLimited,
  remainingExports,
  countdown,
  disabled = false,
}) => {
  const isDisabled = disabled || isLoading || isRateLimited;

  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={onClick}
        disabled={isDisabled}
        data-testid="export-csv-btn"
        aria-describedby={isRateLimited ? "rate-limit-message" : undefined}
        className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:outline-none ${
          isDisabled
            ? "bg-neutral-100 text-neutral-400 cursor-not-allowed"
            : "bg-white text-neutral-700 border border-neutral-300 hover:bg-neutral-50"
        }`}
      >
        {isLoading ? (
          <>
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4 text-neutral-400"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Eksportowanie...
          </>
        ) : (
          <>
            <svg
              className="-ml-1 mr-2 h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            Eksport CSV
          </>
        )}
      </button>

      {/* Remaining exports indicator */}
      <span className="text-xs text-neutral-500" data-testid="remaining-exports">
        Pozostało {remainingExports}/10 eksportów
      </span>

      {/* Rate limit warning */}
      {isRateLimited && countdown !== null && (
        <span
          id="rate-limit-message"
          className="text-xs text-amber-600"
          data-testid="rate-limit-warning"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          Limit osiągnięty. Ponów za {formatCountdown(countdown)}
        </span>
      )}
    </div>
  );
};

export default ExportButton;
